import { x25519 } from '@noble/curves/ed25519';
import { encodeBase64, decodeBase64, randomBytes } from './primitives';
import { wrapKeyForUser } from './pubKeyPinning';
import { loadVaultSession } from './vaultSession';
import { unwrapKey, wrapKey } from './wrappedKey';

export const SEDR_ROOM_CREATE_AAD = 'shareweb:sedr:create';

export function sedrRoomMemberWrapContext(roomId: string): string {
  return `shareweb:sedr:member:${roomId}`;
}

export async function generateSedrRoomKey(): Promise<Uint8Array> {
  return randomBytes(32);
}

export async function wrapSedrRoomKeyForCreator(roomKey: Uint8Array): Promise<string> {
  const session = loadVaultSession();
  if (!session) {
    throw new Error('vault_locked');
  }
  const userPublicKey = x25519.getPublicKey(session.userPrivateKey);
  const wrapped = await wrapKey(roomKey, userPublicKey, SEDR_ROOM_CREATE_AAD);
  return encodeBase64(wrapped);
}

export async function wrapSedrRoomKeyForCreateInvitee(
  roomKey: Uint8Array,
  userId: string,
): Promise<string> {
  const session = loadVaultSession();
  if (!session) {
    throw new Error('vault_locked');
  }
  const wrapped = await wrapKeyForUser(userId, roomKey, SEDR_ROOM_CREATE_AAD);
  return encodeBase64(wrapped);
}

export async function wrapSedrRoomKeyForMember(
  roomId: string,
  roomKey: Uint8Array,
  userId: string,
): Promise<string> {
  const session = loadVaultSession();
  if (!session) {
    throw new Error('vault_locked');
  }
  const wrapped = await wrapKeyForUser(
    userId,
    roomKey,
    sedrRoomMemberWrapContext(roomId),
  );
  return encodeBase64(wrapped);
}

export async function unwrapSedrRoomKey(
  wrappedRoomKeyBase64: string,
  roomId: string,
): Promise<Uint8Array> {
  const session = loadVaultSession();
  if (!session) {
    throw new Error('vault_locked');
  }
  const wrapped = decodeBase64(wrappedRoomKeyBase64);
  const contexts = [SEDR_ROOM_CREATE_AAD, sedrRoomMemberWrapContext(roomId)];
  let lastError: unknown;
  for (const context of contexts) {
    try {
      return await unwrapKey(wrapped, session.userPrivateKey, context);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError ?? new Error('sedr_room_key_unwrap_failed');
}
